import { useCallback } from 'react';

import Grid, { GridSize } from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';

import { genericMemo } from '../../utils';
import { Button } from '../button/Button';
import { useStyles } from './TileToggle.styles';
import { TileToggleProps } from './TileToggle.types';

const TileToggleRow = <V,>(props: TileToggleProps<V>) => {
  const { tiles, value, isMultiple = false, spacing = 2, xs = 6, md = 3, classes, onChange, onBlur } = props;
  const styles = useStyles();

  const handleChange = useCallback(
    (val: V) => {
      if (!onChange) return;

      if (isMultiple) {
        const arr = Array.isArray(value) ? value : [];
        const exists = arr.includes(val);
        const newArr = exists ? arr.filter(v => v !== val) : [...arr, val];
        (onChange as (v: V[]) => void)(newArr);
      } else {
        (onChange as (v: V) => void)(val);
      }

      if (onBlur) onBlur();
    },
    [isMultiple, onChange, value, onBlur],
  );

  return (
    <Grid container direction="row" spacing={spacing}>
      {tiles.map(tile => {
        const isSelected = isMultiple ? Array.isArray(value) && value.includes(tile.value) : value === tile.value;
        return (
          <Grid
            key={`${tile.value}`}
            item
            xs={xs === null ? false : (xs as GridSize)}
            md={md === null ? false : (md as GridSize)}
            className={classes?.tile}
          >
            <Button
              buttonType="text"
              disabled={tile.disabled}
              classes={{ root: styles.tileRoot }}
              className={clsx(styles.tile, classes?.button, isSelected && 'selected', tile.disabled && 'disabled')}
              onClick={() => handleChange(tile.value)}
              onBlur={onBlur}
            >
              <Grid container direction="column" justifyContent="center" alignItems="center">
                <Grid item className={styles.iconContainer}>
                  {tile.icon}
                </Grid>

                <Grid item>
                  <Typography
                    component="span"
                    variant="h5"
                    color="textSecondary"
                    className={clsx(styles.iconText, classes?.label)}
                  >
                    {tile.label}
                  </Typography>
                </Grid>
              </Grid>
            </Button>
          </Grid>
        );
      })}
    </Grid>
  );
};

export const TileToggle = genericMemo(TileToggleRow);
